<script setup>
import { reactive, onMounted, ref, defineProps, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/helper/toast';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import CSATDisplayTypeSelector from './components/CSATDisplayTypeSelector.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import SurveyQuestionsEditor from 'dashboard/components-next/csat/SurveyQuestionsEditor.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');

const isUpdating = ref(false);
const selectedLabelValues = ref([]);
const currentLabel = ref('');

const state = reactive({
  csatSurveyEnabled: false,
  displayType: 'emoji',
  message: '',
  surveyRuleOperator: 'contains',
  mode: 'link',
  questions: [],
  completionMessage: '',
  expiresInHours: 24,
});

const questionsEditor = ref(null);

// No chat ao vivo o modo conversa SUBSTITUI o seletor nativo de nota: é isso
// que permite escala customizada e mais de uma pergunta ali também.
const isWebWidget = computed(
  () => props.inbox?.channel_type === 'Channel::WebWidget'
);

const isChatMode = computed(() => state.mode === 'chat');
const usesDisplayType = computed(
  () => !isChatMode.value || state.questions.length === 0
);

const modeOptions = computed(() => [
  { label: t('INBOX_MGMT.CSAT.CHAT.MODE.LINK'), value: 'link' },
  { label: t('INBOX_MGMT.CSAT.CHAT.MODE.CHAT'), value: 'chat' },
]);

const filterTypes = [
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.CONTAINS'),
    value: 'contains',
  },
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.DOES_NOT_CONTAINS'),
    value: 'does_not_contain',
  },
];

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value
        .map(label => ({ label: label.title, value: label.title }))
        .filter(label => !selectedLabelValues.value.includes(label.value))
    : []
);

const initializeState = () => {
  if (!props.inbox) return;

  const { csat_survey_enabled, csat_config } = props.inbox;

  state.csatSurveyEnabled = csat_survey_enabled || false;

  if (!csat_config) return;

  const {
    display_type: displayType = CSAT_DISPLAY_TYPES.EMOJI,
    message = '',
    survey_rules: surveyRules = {},
    mode = 'link',
    questions = [],
    completion_message: completionMessage = '',
    expires_in_hours: expiresInHours = 24,
  } = csat_config;

  state.displayType = displayType;
  state.message = message;
  state.surveyRuleOperator = surveyRules.operator || 'contains';
  state.mode = mode;
  state.questions = questions.map(question => ({ ...question }));
  state.completionMessage = completionMessage;
  state.expiresInHours = expiresInHours;

  selectedLabelValues.value = Array.isArray(surveyRules.values)
    ? [...surveyRules.values]
    : [];
};

onMounted(() => {
  initializeState();
  if (!labels.value?.length) store.dispatch('labels/get');
});

// Só reinicializa ao TROCAR de caixa. Observar o objeto inteiro fazia o
// formulário ser sobrescrito pelo valor do servidor a cada nova referência do
// mesmo inbox (o store recria o objeto em ActionCable/refetch), apagando o que
// o usuário tinha acabado de preencher — inclusive a chave "Habilitar CSAT",
// que chegava `false` no salvamento.
watch(() => props.inbox?.id, initializeState, { immediate: true });

const handleLabelSelect = value => {
  if (!value || selectedLabelValues.value.includes(value)) {
    return;
  }

  selectedLabelValues.value.push(value);
};

const updateDisplayType = type => {
  state.displayType = type;
};

const updateSurveyRuleOperator = operator => {
  state.surveyRuleOperator = operator;
};

const removeLabel = label => {
  const index = selectedLabelValues.value.indexOf(label);
  if (index !== -1) {
    selectedLabelValues.value.splice(index, 1);
  }
};

const updateInbox = async attributes => {
  const payload = {
    id: props.inbox.id,
    formData: false,
    ...attributes,
  };

  return store.dispatch('inboxes/updateInbox', payload);
};

const saveSettings = async () => {
  if (isChatMode.value && questionsEditor.value) {
    const isValid = await questionsEditor.value.validate();
    // Sem o aviso, o botão "Atualizar" não fazia nada e não dizia nada: parecia
    // que tinha salvado.
    if (!isValid) {
      toast.error(t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.INVALID'));
      return;
    }
  }

  try {
    isUpdating.value = true;

    // O csat_config é gravado inteiro: todo campo do modo chat tem que sair
    // daqui, senão salvar nesta tela apagaria as perguntas em silêncio.
    const csatConfig = {
      display_type: state.displayType,
      message: state.message,
      survey_rules: {
        operator: state.surveyRuleOperator,
        values: selectedLabelValues.value,
      },
      mode: state.mode,
      questions: state.questions,
      completion_message: state.completionMessage,
      expires_in_hours: Number(state.expiresInHours),
    };

    await updateInbox({
      csat_survey_enabled: state.csatSurveyEnabled,
      csat_config: csatConfig,
    });

    toast.success(t('INBOX_MGMT.CSAT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    toast.error(t('INBOX_MGMT.CSAT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('INBOX_MGMT.CSAT.TITLE')"
      :description="$t('INBOX_MGMT.CSAT.SUBTITLE')"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="state.csatSurveyEnabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <WithLabel
          :label="$t('INBOX_MGMT.CSAT.CHAT.MODE.LABEL')"
          name="csat_mode"
        >
          <div class="flex flex-col gap-1">
            <FilterSelect
              v-model="state.mode"
              variant="faded"
              :options="modeOptions"
              class="inline-flex shrink-0 w-fit"
            />
            <p class="text-sm text-n-slate-11">
              {{
                isChatMode
                  ? $t('INBOX_MGMT.CSAT.CHAT.MODE.CHAT_HINT')
                  : $t('INBOX_MGMT.CSAT.CHAT.MODE.LINK_HINT')
              }}
            </p>
            <p v-if="isChatMode && isWebWidget" class="text-sm text-n-amber-11">
              {{ $t('INBOX_MGMT.CSAT.CHAT.MODE.WIDGET_REPLACES_NATIVE') }}
            </p>
          </div>
        </WithLabel>

        <!-- Na conversa sem perguntas configuradas sai o CSAT de sempre (1 a 5),
             então carinhas/estrelas também valem lá. Csat::SurveyDefinition. -->
        <WithLabel
          v-if="usesDisplayType"
          :label="$t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL')"
          name="display_type"
        >
          <CSATDisplayTypeSelector
            :selected-type="state.displayType"
            @update="updateDisplayType"
          />
        </WithLabel>

        <WithLabel :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')" name="message">
          <Editor
            v-model="state.message"
            :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
            :max-length="200"
            class="w-full"
          />
        </WithLabel>

        <template v-if="isChatMode">
          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.LABEL')"
            name="questions"
          >
            <SurveyQuestionsEditor
              ref="questionsEditor"
              v-model="state.questions"
            />
          </WithLabel>

          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.CHAT.COMPLETION_MESSAGE.LABEL')"
            name="completion_message"
          >
            <!-- Várias linhas: o agradecimento costuma levar link e parágrafos
                 (pedido da WebiFibra, conta 443). -->
            <TextArea
              v-model="state.completionMessage"
              class="w-full"
              :max-length="1000"
              auto-height
              show-character-count
              :placeholder="
                $t('INBOX_MGMT.CSAT.CHAT.COMPLETION_MESSAGE.PLACEHOLDER')
              "
            />
          </WithLabel>

          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.CHAT.EXPIRES_IN.LABEL')"
            name="expires_in_hours"
          >
            <div class="flex flex-col gap-1">
              <Input
                v-model="state.expiresInHours"
                type="number"
                class="w-24"
                min="1"
              />
              <p class="text-sm text-n-slate-11">
                {{ $t('INBOX_MGMT.CSAT.CHAT.EXPIRES_IN.HINT') }}
              </p>
            </div>
          </WithLabel>
        </template>

        <WithLabel
          :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL')"
          name="survey_rule"
        >
          <div class="mb-4">
            <span
              class="inline-flex flex-wrap items-center gap-1.5 text-sm text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_PREFIX') }}
              <FilterSelect
                v-model="state.surveyRuleOperator"
                variant="faded"
                :options="filterTypes"
                class="inline-flex shrink-0"
                @update:model-value="updateSurveyRuleOperator"
              />
              {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_SUFFIX') }}

              <NextButton
                v-for="label in selectedLabelValues"
                :key="label"
                sm
                faded
                slate
                trailing-icon
                :label="label"
                icon="i-lucide-x"
                class="inline-flex shrink-0"
                @click="removeLabel(label)"
              />
              <FilterSelect
                v-model="currentLabel"
                :options="labelOptions"
                :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.SELECT_PLACEHOLDER')"
                hide-label
                variant="faded"
                class="inline-flex shrink-0"
                @update:model-value="handleLabelSelect"
              />
            </span>
          </div>
        </WithLabel>
        <p class="text-sm italic text-n-slate-11">
          {{ $t('INBOX_MGMT.CSAT.NOTE') }}
        </p>
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
